/**
 * At most one reaction per interval to org access / role changes (gnexis
 * 2026-10-21).
 *
 * `org:access_changed` / `org:role_changed` reach every signed-in tab of the
 * tenant, one per grant or team edit. An admin ticking teams one after another
 * sends one every second or so, and a script one per call; reacting to each
 * refetched the same lists and permissions over and over, in every member's
 * tab, until the per-user read limit refused everything else. A trailing
 * debounce doesn't help there: clicks are further apart than any short wait.
 *
 * So: a change after a quiet spell reacts `ACCESS_CHANGE_DELAY_MS` later (a
 * revoke shows almost at once, and one edit's events arrive together); while
 * changes keep coming, reactions are at least `ACCESS_CHANGE_MIN_INTERVAL_MS`
 * apart, and the last change is always followed by one — a stream of any pace
 * costs at most one reaction per interval, and nothing is left unreacted.
 *
 * Lives in `@papan/types` (dependency-free, every feature depends on it, rarely
 * mocked) and ships through its index — not a new package subpath: a running
 * Vite caches each package's export map, so a new subpath breaks every dev
 * server until it restarts.
 */

/** How long after a change (following a quiet spell) the reaction runs. */
export const ACCESS_CHANGE_DELAY_MS = 300
/** The least time between two reactions while changes keep coming. */
export const ACCESS_CHANGE_MIN_INTERVAL_MS = 5000

export interface AccessBurst {
  /** A change arrived: make sure a reaction follows it. */
  push(): void
  /** Drop a pending reaction; true when one was pending. */
  cancel(): boolean
}

export function createAccessBurst(react: () => void): AccessBurst {
  let timer: ReturnType<typeof setTimeout> | null = null
  let lastReaction = -Infinity
  const fire = () => {
    timer = null
    lastReaction = performance.now() // monotonic: a wall-clock change can't stretch the interval
    react()
  }
  return {
    push() {
      // A reaction is already due and will see this change too (it reads the
      // server when it runs).
      if (timer) return
      timer = setTimeout(fire, Math.max(ACCESS_CHANGE_DELAY_MS, lastReaction + ACCESS_CHANGE_MIN_INTERVAL_MS - performance.now()))
    },
    cancel() {
      if (!timer) return false
      clearTimeout(timer)
      timer = null
      return true
    },
  }
}

/**
 * Did this access change touch THIS viewer's own access? (#1259) The server marks a change that
 * names one member `affectsViewer: false` on every other member's tab — re-reading permissions
 * there is wasted. Absent (a team / role / folder-wide change, an older server) = yes.
 */
export function changesViewerAccess(event: { affectsViewer?: boolean } | undefined): boolean {
  return event?.affectsViewer !== false
}
